import { Link, useLocation, useNavigate } from "react-router-dom";
import { PanelLeftClose } from "lucide-react";
import { AccountMenu, Sidebar, type SidebarGroup } from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import { GROUP_ORDER, orderRowsInGroup } from "./ManageNavList";
import {
  PAPER_REPORT_ORDER,
  PAPER_SECTION_BADGE,
  PaperRowIcon,
  usePaperNavCounts,
} from "./paperNav";
import { DoodleBadge } from "@/shared/doodles/DoodleBadge";
import { useStoreLogo } from "@/shared/useStoreLogo";
import { setPosTheme, usePosThemeChoice } from "@/shared/posTheme";
import { buildStorefrontUrl } from "@/modules/manage/lib/previewUrl";
import { matchesExactly, type ManageNavGroup, type ManageNavItem } from "./nav";

/**
 * The back-office sidebar — now an ADAPTER over `@a2z/ui`'s `Sidebar`, the one
 * the seller portal renders.
 *
 * It used to be a local aside with its own header, nav and footer. That is the
 * drift the platform rule names by example: two dashboards, two answers to the
 * same frame, neither diff looking wrong on its own. Everything visual now
 * comes from the shared component; this file supplies only what is genuinely
 * this app's — its destinations, its glyphs, its section headings, and a footer
 * carrying language and sign-out.
 *
 * Presentational by contract (decision D-C) still holds: the shell owns the
 * session, the permission filtering and the logout call, and hands this a
 * finished list.
 */
// eslint-disable-next-line no-restricted-syntax -- composes @a2z/ui's Sidebar rather than declaring a rival: this file supplies destinations, glyphs, headings and a footer, and renders none of the frame itself. The name is kept so the shell's call site and the drawer beside it still read as a pair.
export function ManageSidebar({
  storeName,
  userName,
  storeSlug,
  availableStores,
  onSwitchStore,
  isSwitchingStore,
  items,
  onLogout,
  isLoggingOut,
  hidden,
  onToggleHidden,
  canUseTill = false,
}: {
  storeName: string;
  /** The signed-in person. Shown so a short nav list explains itself: a
   *  cashier seeing two sections knows it is because THEY are the cashier —
   *  the founder read the filtered list as missing navigation. */
  userName?: string;
  storeSlug: string;
  availableStores: { storeId: string; storeSlug: string; storeName: string }[];
  onSwitchStore: (slug: string) => void;
  isSwitchingStore: boolean;
  items: ManageNavItem[];
  onLogout: () => void;
  isLoggingOut: boolean;
  /** Collapsed state, OWNED BY THE SHELL — see the note at its call site. */
  hidden: boolean;
  onToggleHidden: () => void;
  /** Holds POS_USE — can open the floor's live view, where PAPER puts the board. */
  canUseTill?: boolean;
}) {
  const t = useTranslate();
  const storeLogo = useStoreLogo();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { locale } = useI18n();
  const theme = usePosThemeChoice();

  const counts = usePaperNavCounts(items);

  const headings: Record<ManageNavGroup, string> = {
    service: t((m) => m.nav.groupService),
    money: t((m) => m.nav.groupMoney),
    menu: t((m) => m.nav.groupMenu),
    inventory: t((m) => m.nav.groupInventory),
    store: t((m) => m.nav.groupStore),
    reports: t((m) => m.nav.groupReports),
  };

  /* Prefix-matching is right for a row whose detail pages are not in the nav —
     open a dish and `المنيو` should stay lit. It is wrong when the nav ALSO
     carries the children: `manage/reports` stayed lit on all five of its
     report pages, so two rows read as active at once and neither said where
     you were. See `matchesExactly`. */
  const isActive = (item: ManageNavItem) =>
    pathname === item.to ||
    (!matchesExactly(item.to, items) && pathname.startsWith(`${item.to}/`));

  // A heading only appears when its section has rows: permission filtering
  // happens upstream, so a cashier who reaches two screens sees two rows under
  // one heading rather than three empty ones.
  /*
   * PAPER reaches the home from the store row at the top of the sidebar, as
   * the redesign does (founder, 2026-10-02), so "Today" is not a row of its
   * own there; and it lists the report rows in the redesign's order.
   */
  const todayItem = items.find((item) => item.rowKey === "today");
  /*
   * Under PAPER the kitchen board is a tab ON the floor's live view (the
   * redesign has no Kitchen row), so its row goes — but only when the session
   * can open that live view (POS_USE): a role with the board and only the
   * floor's layout rights would otherwise lose the board entirely.
   */
  const hasFloor = canUseTill;
  const navItems = items.filter(
    (item) =>
      item.rowKey !== "today" && !(item.rowKey === "kitchen" && hasFloor),
  );
  const sections = GROUP_ORDER.flatMap((group) => {
    const rows = orderRowsInGroup(
      group,
      navItems.filter((item) => item.group === group),
    );
    if (group === "reports") {
      const rank = (item: ManageNavItem) => {
        const at = PAPER_REPORT_ORDER.indexOf(item.rowKey);
        return at === -1 ? PAPER_REPORT_ORDER.length : at;
      };
      rows.sort((a, b) => rank(a) - rank(b));
    }
    return rows.length === 0 ? [] : [{ group, rows }];
  });

  /*
   * PAPER draws the sections as the redesign does: each heading is a row of
   * its own, with a Doodles badge, that folds its destinations away; the
   * destinations carry line icons. Every section starts open, because a nav
   * made only of folded headings shows no destination at all on arrival. The
   * other looks keep the headed list their artboards were measured against.
   */
  const groups: SidebarGroup[] = [
    {
      items: sections.map(({ group, rows }) => ({
        label: headings[group],
        icon: (
          <DoodleBadge
            id={PAPER_SECTION_BADGE[group]}
            size={24}
            animate={false}
          />
        ),
        // The heading is never "where you are" — its row is.
        isActive: false,
        defaultOpen: true,
        subItems: rows.map((item) => ({
          href: item.to,
          label: item.label,
          icon: <PaperRowIcon rowKey={item.rowKey} />,
          isActive: isActive(item),
          count: counts[item.rowKey] ?? null,
        })),
      })),
    },
  ];

  const settingsHref = items.find((item) => item.to.endsWith("/settings"))?.to;
  /**
   * Where the logo and the "home" affordance point.
   *
   * The fallback was `""`, which is not "nowhere" — an empty `href` is the
   * CURRENT url, so a session with no rows got a logo that silently reloaded
   * the page it was already on. `/manage` is the honest target: it re-decides
   * per permission, and for a session with nothing to see it renders the
   * no-access explanation rather than pretending to navigate.
   */
  const dashboardHref = items[0]?.to ?? `/${locale}/manage`;

  // Absolute, so a paste works anywhere — the helper answers a bare host
  // (localhost) with a path.
  const storefrontLink = buildStorefrontUrl({ storeSlug, locale });
  const menuLink = storefrontLink.startsWith("/")
    ? `${window.location.origin}${storefrontLink}`
    : storefrontLink || storeSlug;

  return (
    <div className="no-print hidden lg:flex">
      <Sidebar
        dashboardHref={dashboardHref}
        settingsHref={settingsHref ?? dashboardHref}
        groups={groups}
        homeHref={todayItem?.to}
        collapseIcon={
          // The redesign's mark: the panel with a chevron pointing it shut,
          // mirrored in Arabic so it still points at the sidebar.
          <PanelLeftClose
            size={16}
            strokeWidth={1.75}
            aria-hidden="true"
            className="rtl:-scale-x-100"
          />
        }
        hidden={hidden}
        onHide={onToggleHidden}
        // Western digits in both locales (AGENTS.md): the shared sidebar
        // would otherwise draw its counts Arabic-Indic from <html lang="ar">.
        locale={locale === "ar" ? "ar-EG-u-nu-latn" : locale}
        identity={{
          storeName,
          // The restaurant session carries no short code; the shared component
          // treats it as optional and falls back to the monogram.
          storeShort: null,
          initial: storeName.trim().charAt(0) || "?",
          // The top tile is TRIOTY's mark, white on black (founder,
          // 2026-10-07); the restaurant's own logo is the account avatar at the
          // foot, below.
          logoUrl: "/logo-mark.svg",
        }}
        availableStores={availableStores.map((store) => ({
          ...store,
          storeShort: "",
        }))}
        activeStoreSlug={storeSlug}
        onSwitchStore={onSwitchStore}
        isSwitchingStore={isSwitchingStore}
        labels={{
          appName: t((m) => m.common.appName),
          collapse: t((m) => m.nav.collapse),
          hintSetup: t((m) => m.nav.label),
          storeLive: t((m) => m.common.appName),
          storeSettings: t((m) => m.nav.groupStore),
          switchStore: t((m) => m.nav.switchStore),
          currentStore: t((m) => m.nav.currentStore),
        }}
        renderLink={({ href, children, ...rest }) => (
          <Link to={href} {...rest}>
            {children}
          </Link>
        )}
        logo={
          <img
            src="/logo-mark.svg"
            alt={t((m) => m.common.appName)}
            className="h-7 w-auto object-contain"
          />
        }
        accountSlot={
          <AccountMenu
            storeName={storeName}
            storeShort={null}
            // The copy button hands over the restaurant's diner page — the
            // link a guest opens (founder, 2026-10-07: "the copy button
            // suppose copy the buyer portal"), the same one the Storefront
            // page opens and the table QR codes point at.
            storeCode={menuLink}
            sellerName={userName ?? storeName}
            // The restaurant session carries no email — the row falls back to
            // the store name rather than showing an empty line.
            sellerEmail={storeName}
            // The foot is the RESTAURANT (founder, 2026-10-07): its logo, or
            // its initial when it has none, beside who is signed in.
            initial={storeName.trim().charAt(0) || "?"}
            avatarUrl={storeLogo}
            locale={locale}
            onLocaleChange={(next) =>
              navigate(`${pathname.replace(/^\/[^/]+/, `/${next}`)}${search}`)
            }
            // No personal-info or password screen in this portal yet, so those
            // rows are omitted rather than rendered inert.
            isDark={theme === "dark"}
            onThemeChange={(next) => setPosTheme(next === "dark" ? "dark" : "light")}
            onLogout={onLogout}
            isLoggingOut={isLoggingOut}
            labels={{
              storeMenu: t((m) => m.common.appName),
              live: t((m) => m.common.appName),
              copyCode: t((m) => m.nav.copyMenuLink),
              codeCopied: t((m) => m.nav.menuLinkCopied),
              storeShortTooltip: t((m) => m.nav.switchStore),
              language: t((m) => m.pos.language),
              theme: t((m) => m.pos.theme),
              themeLight: t((m) => m.nav.themeLight),
              themeDark: t((m) => m.nav.themeDark),
              themeSystem: t((m) => m.nav.themeSystem),
              logout: t((m) => m.common.logout),
            }}
          />
        }
      />
    </div>
  );
}
